import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewProduction = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState('recipe'); // 'recipe' o 'product'
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [preview, setPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    recipe_id: '',
    product_id: '',
    bottle_format_cl: 75,
    quantity_bottles: '',
    batch_number: '',
    customer: '',
    operator: '',
    production_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const loadRecipes = async () => {
    if (!token) return;
    
    try {
      console.log('Loading recipes...');
      const response = await axios.get(`${API}/recipes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Recipes loaded:', response.data.length, 'recipes');
      setRecipes(response.data);
    } catch (error) {
      console.error('Errore caricamento ricette:', error);
      toast.error('Errore nel caricamento delle ricette');
    }
  };

  const loadProducts = async () => {
    if (!token) return;
    
    try {
      console.log('Loading products...');
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Products loaded:', response.data.length, 'products');
      setProducts(response.data);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
      toast.error('Errore nel caricamento dei prodotti');
    }
  };

  const generateBatchNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({
      ...prev,
      batch_number: `LOTTO-${year}${month}${day}-${random}`
    }));
  };

  useEffect(() => {
    generateBatchNumber();
  }, []);

  useEffect(() => {
    if (token) {
      loadRecipes();
      loadProducts();
    }
  }, [token]);

  const handleCalculate = async () => {
    // Validazione in base alla modalità
    if (mode === 'recipe') {
      if (!formData.recipe_id || !formData.bottle_format_cl || !formData.quantity_bottles) {
        toast.error('Compila ricetta, formato e quantità per calcolare');
        return;
      }
    } else {
      if (!formData.product_id || !formData.quantity_bottles) {
        toast.error('Compila prodotto e quantità per calcolare');
        return;
      }
    }

    setCalculating(true);
    try {
      const params = {
        quantity_bottles: formData.quantity_bottles
      };

      if (mode === 'recipe') {
        params.recipe_id = formData.recipe_id;
        params.bottle_format_cl = formData.bottle_format_cl;
      } else {
        params.product_id = formData.product_id;
      }

      const response = await axios.post(
        `${API}/productions/calculate`,
        null,
        {
          params,
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPreview(response.data);
      toast.success('Calcolo completato!');
    } catch (error) {
      console.error('Errore calcolo:', error);
      toast.error('Errore nel calcolo delle quantità');
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!preview) {
      toast.error('Calcola le quantità prima di salvare');
      return;
    }

    if (!formData.batch_number || !formData.customer || !formData.operator) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setLoading(true);
    try {
      // Prepara dati in base alla modalità
      const productionData = {
        quantity_bottles: formData.quantity_bottles,
        batch_number: formData.batch_number,
        customer: formData.customer,
        operator: formData.operator,
        production_date: formData.production_date,
        notes: formData.notes
      };

      if (mode === 'recipe') {
        productionData.recipe_id = formData.recipe_id;
        productionData.bottle_format_cl = formData.bottle_format_cl;
      } else {
        productionData.product_id = formData.product_id;
      }

      const response = await axios.post(
        `${API}/productions`,
        productionData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Produzione creata con successo!');
      
      // Download PDF
      const pdfResponse = await axios.get(
        `${API}/productions/${response.data.production_id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Produzione_${formData.batch_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Redirect allo storico
      setTimeout(() => navigate('/productions/history'), 1000);
    } catch (error) {
      console.error('Errore creazione:', error);
      toast.error('Errore nella creazione della produzione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Nuova Produzione</h1>
        <p className="text-gray-600">Crea una nuova scheda di produzione</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Dati Produzione */}
          <Card>
            <CardHeader>
              <CardTitle>Dati Produzione</CardTitle>
              <CardDescription>Informazioni base della produzione</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Toggle Modalità */}
              <div className="space-y-2">
                <Label>Modalità Produzione</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={mode === 'recipe' ? 'default' : 'outline'}
                    onClick={() => {
                      setMode('recipe');
                      setFormData({...formData, recipe_id: '', product_id: ''});
                      setPreview(null);
                    }}
                    className="flex-1"
                  >
                    📝 Da Ricetta
                  </Button>
                  <Button
                    type="button"
                    variant={mode === 'product' ? 'default' : 'outline'}
                    onClick={() => {
                      setMode('product');
                      setFormData({...formData, recipe_id: '', product_id: ''});
                      setPreview(null);
                    }}
                    className="flex-1"
                  >
                    📦 Da Prodotto
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  {mode === 'recipe' 
                    ? 'Solo ingredienti - formato bottiglia personalizzato'
                    : 'Ingredienti + Packaging - formato predefinito dal prodotto'}
                </p>
              </div>

              {/* Selezione Ricetta o Prodotto */}
              {mode === 'recipe' ? (
                <div className="space-y-2">
                  <Label htmlFor="recipe">Ricetta * ({recipes.length} disponibili)</Label>
                  <select
                    id="recipe"
                    value={formData.recipe_id}
                    onChange={(e) => {
                      setFormData({...formData, recipe_id: e.target.value, product_id: ''});
                      setPreview(null);
                    }}
                    className="w-full p-2 border rounded-md"
                    required
                  >
                    <option value="">Seleziona una ricetta...</option>
                    {recipes.map((recipe) => (
                      <option key={recipe.id} value={recipe.id}>
                        {recipe.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="product">Prodotto * ({products.length} disponibili)</Label>
                  <select
                    id="product"
                    value={formData.product_id}
                    onChange={(e) => {
                      setFormData({...formData, product_id: e.target.value, recipe_id: ''});
                      setPreview(null);
                    }}
                    className="w-full p-2 border rounded-md"
                    required
                  >
                    <option value="">Seleziona un prodotto...</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {product.bottle_volume_ml}ml
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {mode === 'recipe' && (
                  <div className="space-y-2">
                    <Label htmlFor="format">Formato Bottiglia (cl) *</Label>
                    <Input
                      id="format"
                      type="number"
                      min="5"
                      step="5"
                      value={formData.bottle_format_cl}
                      onChange={(e) => {
                        setFormData({...formData, bottle_format_cl: parseInt(e.target.value)});
                        setPreview(null);
                      }}
                      required
                    />
                    <p className="text-xs text-gray-500">Es: 75, 100, 150 cl</p>
                  </div>
                )}

                <div className={`space-y-2 ${mode === 'product' ? 'col-span-2' : ''}`}>
                  <Label htmlFor="quantity">Quantità Bottiglie *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity_bottles}
                    onChange={(e) => {
                      setFormData({...formData, quantity_bottles: parseInt(e.target.value)});
                      setPreview(null);
                    }}
                    required
                  />
                </div>
              </div>

              <Button
                type="button"
                onClick={handleCalculate}
                disabled={calculating || (mode === 'recipe' ? !formData.recipe_id : !formData.product_id) || !formData.quantity_bottles}
                className="w-full"
                variant="outline"
              >
                {calculating ? 'Calcolo...' : '🔢 Calcola Quantità'}
              </Button>

              <div className="space-y-2">
                <Label htmlFor="batch">Numero Lotto *</Label>
                <div className="flex gap-2">
                  <Input
                    id="batch"
                    value={formData.batch_number}
                    onChange={(e) => setFormData({...formData, batch_number: e.target.value})}
                    required
                  />
                  <Button type="button" onClick={generateBatchNumber} variant="outline">
                    Genera
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Cliente *</Label>
                <Input
                  id="customer"
                  value={formData.customer}
                  onChange={(e) => setFormData({...formData, customer: e.target.value})}
                  placeholder="Nome cliente"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="operator">Operatore *</Label>
                <Input
                  id="operator"
                  value={formData.operator}
                  onChange={(e) => setFormData({...formData, operator: e.target.value})}
                  placeholder="Nome operatore"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Data Produzione *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.production_date}
                  onChange={(e) => setFormData({...formData, production_date: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full p-2 border rounded-md min-h-[80px]"
                  placeholder="Note aggiuntive..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Anteprima Calcoli */}
          <div className="space-y-6">
            {preview ? (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>Ingredienti Necessari</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {preview.ingredients_needed.map((ing, idx) => (
                        <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="font-medium">{ing.ingredient_name}</span>
                          <span className="text-blue-600">
                            {ing.quantity} {ing.unit} ({ing.percentage}%)
                          </span>
                        </div>
                      ))}
                      <div className="pt-2 border-t font-bold flex justify-between">
                        <span>Totale:</span>
                        <span>{preview.total_liters} L</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Packaging Necessario</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {preview.packaging_needed && preview.packaging_needed.length > 0 ? (
                      <div className="space-y-2">
                        {preview.packaging_needed.map((pkg, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span className="font-medium">{pkg.packaging_name}</span>
                            <span className="text-green-600">
                              {pkg.quantity} {pkg.unit}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        {mode === 'recipe' 
                          ? 'Modalità ricetta - packaging non calcolato'
                          : 'Nessun packaging configurato per questo prodotto'}
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-blue-900">Costi Totali</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span>Ingredienti:</span>
                        <span>€ {preview.cost_breakdown.ingredients.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Packaging:</span>
                        <span>€ {preview.cost_breakdown.packaging.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg pt-2 border-t border-blue-300">
                        <span>TOTALE:</span>
                        <span className="text-blue-700">€ {preview.total_cost.toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center text-gray-500 py-12">
                  <p className="text-lg mb-2">👆 Calcola le quantità</p>
                  <p className="text-sm">per vedere l'anteprima</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/productions/history')}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={loading || !preview}
          >
            {loading ? 'Salvataggio...' : '💾 Salva e Stampa PDF'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewProduction;
